// src/hooks/useSideMenuLogic.js
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleAuthProvider, OAuthProvider, signInWithPopup, signOut, deleteUser } from 'firebase/auth';
import { auth } from '../firebase-config';
import { useAuth } from '../contexts/authContext';
import { API_URL } from '../lib/config';
import { useSocket } from '../contexts/socketContext';

export const useSideMenuLogic = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const socket = useSocket(); // ★ 소켓 객체 가져오기

  const toggleMenu = () => setIsMenuOpen(prev => !prev);
  const togglePopup = () => setIsPopupOpen(prev => !prev);

  // 네비게이션 핸들러
  const handleProfileClick = () => { navigate('/profile'); setIsMenuOpen(false); };
  const handleInboxClick = () => { navigate('/inbox'); setIsMenuOpen(false); };
  const handleRealtimeChatClick = () => { navigate('/matching'); setIsMenuOpen(false); };
  const handleHomeClick = () => { navigate('/'); setIsMenuOpen(false); };
  const handleNoticeClick = () => { navigate('/notices'); setIsMenuOpen(false); };
  const handleSettingsClick = () => { navigate('/settings'); setIsMenuOpen(false); };
  const handleFriendListClick = () => { navigate('/friend-list'); setIsMenuOpen(false); };

  // 로그인 핸들러 (게스트 -> 정회원 전환 포함)
  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      // 1. 게스트 UID 백업 (마이그레이션용)
      const guestUid = currentUser?.isAnonymous ? currentUser.uid : null;
      
      const result = await signInWithPopup(auth, provider);
      const newUser = result.user;

      // 2. 게스트였다면 마이그레이션 요청
      if (guestUid) {
        const idToken = await newUser.getIdToken();
        await fetch(`${API_URL}/api/login/migrate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
          body: JSON.stringify({ guestUid })
        });
      }
      setIsPopupOpen(false);
      setIsMenuOpen(false);
    } catch (error) {
      console.error("로그인 오류:", error);
    }
  };

  // ▼▼▼ [신규 추가] 애플 로그인 핸들러 (게스트 -> 정회원 전환 포함) ▼▼▼
  const handleAppleLogin = async () => {
    const provider = new OAuthProvider('apple.com');
    
    // 애플 로그인 시 이메일과 이름을 받아오기 위한 스코프 설정
    provider.addScope('email');
    provider.addScope('name');
    provider.setCustomParameters({
      locale: 'ko', // 한국어 로그인 창 띄우기
    });

    try {
      // 1. 게스트 UID 백업 (마이그레이션용)
      const guestUid = currentUser?.isAnonymous ? currentUser.uid : null;
      
      const result = await signInWithPopup(auth, provider);
      const newUser = result.user;

      // 2. 게스트였다면 마이그레이션 요청
      if (guestUid) {
        const idToken = await newUser.getIdToken();
        await fetch(`${API_URL}/api/login/migrate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
          body: JSON.stringify({ guestUid })
        });
      }
      setIsPopupOpen(false);
      setIsMenuOpen(false);
    } catch (error) {
      console.error("애플 로그인 오류:", error);
      // 유저가 로그인 창을 그냥 닫았을 때 발생하는 에러 등은 무시해도 됩니다.
    }
  };
  // ▲▲▲ 여기까지 신규 추가 ▲▲▲

  // 로그아웃
  const handleLogout = async () => {
    try {
      if (socket && socket.connected) {
        // console.log("로그아웃: 소켓 연결을 종료합니다.");
        socket.emit('cancel_matching'); // 혹시 대기 중이었다면 취소 요청
        socket.disconnect(); // 연결 끊기 (서버의 disconnect 핸들러 발동 -> 청소됨)
      }
      await signOut(auth);
      setIsMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error("로그아웃 오류:", error);
    }
  };

  // 회원 탈퇴
  const handleDeleteAccount = async () => {
    if (!currentUser) return;
    if (!window.confirm("정말로 회원 탈퇴를 진행하시겠습니까?")) return;
    
    const input = window.prompt("데이터를 삭제하려면 '회원탈퇴'라고 입력해주세요.");
    if (input !== "회원탈퇴") return alert("입력이 일치하지 않습니다.");

    try {
      const idToken = await currentUser.getIdToken();
      await fetch(`${API_URL}/api/login/delete-account`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      await deleteUser(currentUser);
      alert("탈퇴가 완료되었습니다.");
      setIsMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error(error);
      alert("탈퇴 처리 중 오류가 발생했습니다.");
    }
  };

  return {
    isMenuOpen,
    isPopupOpen,
    toggleMenu,
    togglePopup,
    handleGoogleLogin,
    handleAppleLogin,
    handleLogout,
    handleDeleteAccount,
    handleProfileClick,
    handleInboxClick,
    handleRealtimeChatClick,
    handleHomeClick,
    handleNoticeClick,
    handleSettingsClick,
    handleFriendListClick
  };
};